/** Activity log entity types (match API AdminActivityEntityTypes). */
export const FreeQuotaActivityEntityTypes = {
  SiteFreeQuota: 'city_parking_site_free_quota',
  ClientFreeQuota: 'city_parking_client_free_quota',
  UserSiteAccess: 'city_parking_user_site_access',
} as const;

export type FreeQuotaActivityEntityType =
  (typeof FreeQuotaActivityEntityTypes)[keyof typeof FreeQuotaActivityEntityTypes];

/** Entity id for ModalAdminActivityHistoryComponent (row primary key). */
export function freeQuotaActivityEntityId(id: number | null | undefined): number {
  return id != null && id > 0 ? id : 0;
}

export interface SiteFreeQuotaDto {
  id?: number;
  Id?: number;
  siteId?: number;
  SiteId?: number;
  vehicleTypeId?: number;
  VehicleTypeId?: number;
  vehicleTypeName?: string | null;
  VehicleTypeName?: string | null;
  subscriptionId?: number | null;
  SubscriptionId?: number | null;
  subscriptionName?: string | null;
  SubscriptionName?: string | null;
  quotaApproved?: number;
  QuotaApproved?: number;
  quotaUsed?: number;
  QuotaUsed?: number;
  quotaAllocatedToClients?: number;
  QuotaAllocatedToClients?: number;
  quotaRemainingDirect?: number;
  QuotaRemainingDirect?: number;
  quotaRemainingPool?: number;
  QuotaRemainingPool?: number;
  status?: string;
  Status?: string;
}

export interface SiteFreeQuota {
  id: number;
  siteId: number;
  vehicleTypeId: number;
  vehicleTypeName: string;
  subscriptionId: number | null;
  subscriptionName: string;
  quotaApproved: number;
  quotaUsed: number;
  quotaAllocatedToClients: number;
  quotaRemainingDirect: number;
  quotaRemainingPool: number;
  status: string;
}

export interface ClientFreeQuotaDto {
  id?: number;
  Id?: number;
  siteId?: number;
  SiteId?: number;
  clientId?: number;
  ClientId?: number;
  clientName?: string | null;
  ClientName?: string | null;
  vehicleTypeId?: number;
  VehicleTypeId?: number;
  vehicleTypeName?: string | null;
  VehicleTypeName?: string | null;
  parentSiteQuotaId?: number;
  ParentSiteQuotaId?: number;
  quotaApproved?: number;
  QuotaApproved?: number;
  quotaUsed?: number;
  QuotaUsed?: number;
  quotaRemaining?: number;
  QuotaRemaining?: number;
  managedBy?: string;
  ManagedBy?: string;
}

export interface ClientFreeQuota {
  id: number;
  siteId: number;
  clientId: number;
  clientName: string;
  vehicleTypeId: number;
  vehicleTypeName: string;
  parentSiteQuotaId: number;
  quotaApproved: number;
  quotaUsed: number;
  quotaRemaining: number;
  managedBy: string;
}

export interface UserSiteAccessDto {
  id?: number;
  Id?: number;
  resUserId?: number;
  ResUserId?: number;
  resUserName?: string | null;
  ResUserName?: string | null;
  siteId?: number;
  SiteId?: number;
  siteName?: string | null;
  SiteName?: string | null;
  clientId?: number | null;
  ClientId?: number | null;
  clientName?: string | null;
  ClientName?: string | null;
  active?: boolean;
  Active?: boolean;
  canManageQuota?: boolean;
  CanManageQuota?: boolean;
  canDeleteCardReturnQuota?: boolean;
  CanDeleteCardReturnQuota?: boolean;
}

export interface UserSiteAccess {
  id: number;
  resUserId: number;
  resUserName: string;
  siteId: number;
  siteName: string;
  clientId: number | null;
  clientName: string;
  active: boolean;
  canManageQuota: boolean;
  canDeleteCardReturnQuota: boolean;
}

export interface SaveSiteFreeQuotaPayload {
  userId: number;
  menuId: number;
  id?: number | null;
  siteId: number;
  vehicleTypeId: number;
  subscriptionId?: number | null;
  quotaApproved: number;
  status?: string | null;
}

export interface AllocateClientFreeQuotaPayload {
  userId: number;
  menuId: number;
  siteId: number;
  clientId: number;
  vehicleTypeId: number;
  additionalQuota: number;
  managedBy?: string | null;
}

export interface UpdateClientFreeQuotaPayload {
  userId: number;
  menuId: number;
  id: number;
  quotaApproved: number;
  managedBy?: string | null;
}

export interface SaveUserSiteAccessPayload {
  userId: number;
  menuId: number;
  id?: number | null;
  resUserId: number;
  siteId: number;
  clientId?: number | null;
  active: boolean;
  canManageQuota: boolean;
  canDeleteCardReturnQuota: boolean;
}

export const SITE_FREE_QUOTA_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'active', label: 'ใช้งาน (active)' },
  { value: 'suspended', label: 'ระงับ (suspended)' },
];

export const CLIENT_FREE_QUOTA_MANAGED_BY_OPTIONS: { value: string; label: string }[] = [
  { value: 'site_building', label: 'ฝ่ายอาคาร (site_building)' },
  { value: 'client_self', label: 'ลูกค้าจัดการเอง (client_self)' },
];

function num(raw: Record<string, unknown>, ...keys: string[]): number {
  for (const k of keys) {
    const v = raw[k];
    if (v !== undefined && v !== null && v !== '') return Number(v);
  }
  return 0;
}

function str(raw: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = raw[k];
    if (v !== undefined && v !== null) return String(v);
  }
  return '';
}

function bool(raw: Record<string, unknown>, ...keys: string[]): boolean {
  for (const k of keys) {
    const v = raw[k];
    if (v !== undefined && v !== null) return Boolean(v);
  }
  return false;
}

function nullableNum(raw: Record<string, unknown>, ...keys: string[]): number | null {
  for (const k of keys) {
    const v = raw[k];
    if (v === undefined || v === null || v === '') continue;
    const n = Number(v);
    return n > 0 ? n : null;
  }
  return null;
}

export function mapSiteFreeQuotaFromDto(dto: SiteFreeQuotaDto): SiteFreeQuota {
  const raw = dto as SiteFreeQuotaDto & Record<string, unknown>;
  return {
    id: num(raw, 'id', 'Id'),
    siteId: num(raw, 'siteId', 'SiteId'),
    vehicleTypeId: num(raw, 'vehicleTypeId', 'VehicleTypeId'),
    vehicleTypeName: str(raw, 'vehicleTypeName', 'VehicleTypeName'),
    subscriptionId: nullableNum(raw, 'subscriptionId', 'SubscriptionId'),
    subscriptionName: str(raw, 'subscriptionName', 'SubscriptionName'),
    quotaApproved: num(raw, 'quotaApproved', 'QuotaApproved'),
    quotaUsed: num(raw, 'quotaUsed', 'QuotaUsed'),
    quotaAllocatedToClients: num(raw, 'quotaAllocatedToClients', 'QuotaAllocatedToClients'),
    quotaRemainingDirect: num(raw, 'quotaRemainingDirect', 'QuotaRemainingDirect'),
    quotaRemainingPool: num(raw, 'quotaRemainingPool', 'QuotaRemainingPool'),
    status: str(raw, 'status', 'Status') || 'active',
  };
}

export function mapClientFreeQuotaFromDto(dto: ClientFreeQuotaDto): ClientFreeQuota {
  const raw = dto as ClientFreeQuotaDto & Record<string, unknown>;
  return {
    id: num(raw, 'id', 'Id'),
    siteId: num(raw, 'siteId', 'SiteId'),
    clientId: num(raw, 'clientId', 'ClientId'),
    clientName: str(raw, 'clientName', 'ClientName'),
    vehicleTypeId: num(raw, 'vehicleTypeId', 'VehicleTypeId'),
    vehicleTypeName: str(raw, 'vehicleTypeName', 'VehicleTypeName'),
    parentSiteQuotaId: num(raw, 'parentSiteQuotaId', 'ParentSiteQuotaId'),
    quotaApproved: num(raw, 'quotaApproved', 'QuotaApproved'),
    quotaUsed: num(raw, 'quotaUsed', 'QuotaUsed'),
    quotaRemaining: num(raw, 'quotaRemaining', 'QuotaRemaining'),
    managedBy: str(raw, 'managedBy', 'ManagedBy'),
  };
}

export function mapUserSiteAccessFromDto(dto: UserSiteAccessDto): UserSiteAccess {
  const raw = dto as UserSiteAccessDto & Record<string, unknown>;
  const clientId = nullableNum(raw, 'clientId', 'ClientId');
  return {
    id: num(raw, 'id', 'Id'),
    resUserId: num(raw, 'resUserId', 'ResUserId'),
    resUserName: str(raw, 'resUserName', 'ResUserName'),
    siteId: num(raw, 'siteId', 'SiteId'),
    siteName: str(raw, 'siteName', 'SiteName'),
    clientId,
    clientName: str(raw, 'clientName', 'ClientName'),
    active: bool(raw, 'active', 'Active'),
    canManageQuota: bool(raw, 'canManageQuota', 'CanManageQuota'),
    canDeleteCardReturnQuota: bool(raw, 'canDeleteCardReturnQuota', 'CanDeleteCardReturnQuota'),
  };
}

export function mapSiteFreeQuotaList(data: unknown): SiteFreeQuota[] {
  const list = Array.isArray(data) ? data : [];
  return list.map((item) => mapSiteFreeQuotaFromDto(item as SiteFreeQuotaDto));
}

export function mapClientFreeQuotaList(data: unknown): ClientFreeQuota[] {
  const list = Array.isArray(data) ? data : [];
  return list.map((item) => mapClientFreeQuotaFromDto(item as ClientFreeQuotaDto));
}

export function mapUserSiteAccessList(data: unknown): UserSiteAccess[] {
  const list = Array.isArray(data) ? data : [];
  return list.map((item) => mapUserSiteAccessFromDto(item as UserSiteAccessDto));
}

export interface FreeParkingSubscriptionOptionDto {
  id?: number;
  Id?: number;
  name?: string | null;
  Name?: string | null;
  vehicleTypeId?: number | null;
  VehicleTypeId?: number | null;
  vehicleTypeName?: string | null;
  VehicleTypeName?: string | null;
}

export interface FreeParkingSubscriptionOption {
  id: number;
  name: string;
  vehicleTypeId: number | null;
  vehicleTypeName: string;
}

export function mapFreeParkingSubscriptionList(data: unknown): FreeParkingSubscriptionOption[] {
  const list = Array.isArray(data) ? data : [];
  return list.map((item) => {
    const raw = item as FreeParkingSubscriptionOptionDto & Record<string, unknown>;
    return {
      id: num(raw, 'id', 'Id'),
      name: str(raw, 'name', 'Name') || `#${num(raw, 'id', 'Id')}`,
      vehicleTypeId: nullableNum(raw, 'vehicleTypeId', 'VehicleTypeId'),
      vehicleTypeName: str(raw, 'vehicleTypeName', 'VehicleTypeName'),
    };
  });
}

export function createEmptySiteFreeQuotaForm(siteId: number): SaveSiteFreeQuotaPayload {
  return {
    userId: 0,
    menuId: 0,
    id: null,
    siteId,
    vehicleTypeId: 0,
    subscriptionId: null,
    quotaApproved: 0,
    status: 'active',
  };
}

/* ---------- Client quota table (server paging + search) ---------- */

export interface ClientFreeQuotaSummaryDto {
  rowCount?: number;
  clientCount?: number;
  quotaApproved?: number;
  quotaUsed?: number;
  quotaRemaining?: number;
}

export interface ClientFreeQuotaTableResponse {
  data?: ClientFreeQuotaDto[];
  total?: number;
  page?: number;
  pageSize?: number;
  totalPages?: number;
  summary?: ClientFreeQuotaSummaryDto | null;
}

export interface ClientFreeQuotaSummary {
  rowCount: number;
  clientCount: number;
  quotaApproved: number;
  quotaUsed: number;
  quotaRemaining: number;
}

export function mapClientFreeQuotaSummary(dto: ClientFreeQuotaSummaryDto | null | undefined): ClientFreeQuotaSummary {
  return {
    rowCount: Number(dto?.rowCount ?? 0),
    clientCount: Number(dto?.clientCount ?? 0),
    quotaApproved: Number(dto?.quotaApproved ?? 0),
    quotaUsed: Number(dto?.quotaUsed ?? 0),
    quotaRemaining: Number(dto?.quotaRemaining ?? 0),
  };
}

export interface FreeQuotaClientOptionDto {
  id?: number;
  name?: string | null;
  taxId?: string | null;
  contact?: string | null;
  telephone?: string | null;
  quotaApproved?: number;
  quotaUsed?: number;
}

export interface FreeQuotaClientOption {
  id: number;
  name: string;
  taxId: string;
  contact: string;
  telephone: string;
  quotaApproved: number;
  quotaUsed: number;
}

export function mapFreeQuotaClientOptionList(data: unknown): FreeQuotaClientOption[] {
  const list = Array.isArray(data) ? (data as FreeQuotaClientOptionDto[]) : [];
  return list
    .map((x) => {
      const id = Number(x.id ?? 0);
      return {
        id,
        name: x.name || `Client #${id}`,
        taxId: x.taxId ?? '',
        contact: x.contact ?? '',
        telephone: x.telephone ?? '',
        quotaApproved: Number(x.quotaApproved ?? 0),
        quotaUsed: Number(x.quotaUsed ?? 0),
      };
    })
    .filter((x) => x.id > 0);
}

export function clientFreeQuotaManagedByLabel(value: string): string {
  return value === 'client_self' ? 'ลูกค้าจัดการเอง' : 'ฝ่ายอาคาร';
}

export function createEmptyUserSiteAccessForm(siteId: number): SaveUserSiteAccessPayload {
  return {
    userId: 0,
    menuId: 0,
    id: null,
    resUserId: 0,
    siteId,
    clientId: null,
    active: true,
    canManageQuota: false,
    canDeleteCardReturnQuota: false,
  };
}
